import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import { appApi, useGetAllStaffQuery } from "../../store/appApi";
import { allotImprestApi } from "../../services/imprest";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Shared "Transfer to Employee" (allot imprest from the Daybook) modal —
// used from both the Daybook list and the Add Expense page, so the flow and
// its balance display stay in exactly one place.
const AllotImprestModal = ({ show, onClose, currentBalance }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    employeeId: "",
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !show },
  );
  const employeeOptions = Array.isArray(staffData?.staff)
    ? staffData.staff.filter((item) => item?.status !== false)
    : [];

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleClose = () => {
    setForm({
      employeeId: "",
      date: new Date().toISOString().slice(0, 10),
      amount: "",
      remarks: "",
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employeeId) {
      Swal.fire({
        icon: "warning",
        title: "Select employee",
        text: "Please choose an employee to transfer to.",
      });
      return;
    }
    const numericAmount = Number(form.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }
    if (numericAmount > currentBalance) {
      Swal.fire({
        icon: "warning",
        title: "Amount too high",
        text: `Only ${fmtINR(currentBalance)} is available in the Daybook.`,
      });
      return;
    }

    try {
      setSaving(true);
      await allotImprestApi({
        employeeId: form.employeeId,
        date: form.date
          ? new Date(form.date).toISOString()
          : new Date().toISOString(),
        amount: numericAmount,
        remarks: form.remarks,
      });
      dispatch(appApi.util.invalidateTags(["Expenses", "Imprest"]));
      Swal.fire({
        icon: "success",
        title: "Transferred",
        text: `${fmtINR(numericAmount)} moved to the employee's daybook.`,
        timer: 1800,
        showConfirmButton: false,
      });
      handleClose();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to transfer",
        text:
          error?.response?.data?.message ||
          "Something went wrong while transferring this amount.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Transfer to Employee</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          This debits the Daybook and credits the employee's own daybook
          (imprest) directly.
        </p>
        <form
          id="allot-imprest-form"
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.type !== "submit") {
              e.preventDefault();
            }
          }}
        >
          <div className="form-group mb-3">
            <label className="form-label">
              Employee <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={form.employeeId}
              onChange={(e) => updateField("employeeId", e.target.value)}
            >
              <option value="">Select employee</option>
              {employeeOptions.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.name} -{" "}
                  {String(employee.role || "").replace(/-/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group mb-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => updateField("date", e.target.value)}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="form-group mb-3">
                <label className="form-label">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max={currentBalance}
                  step="0.01"
                  className="form-control"
                  value={form.amount}
                  onChange={(e) => updateField("amount", e.target.value)}
                />
                <small className="text-muted d-block mt-1">
                  Available balance: <strong>{fmtINR(currentBalance)}</strong>
                </small>
              </div>
            </div>
          </div>
          <div className="form-group mb-1">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-control"
              value={form.remarks}
              onChange={(e) => updateField("remarks", e.target.value)}
              placeholder="Optional note"
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="allot-imprest-form"
          disabled={saving || currentBalance <= 0}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Transferring…
            </>
          ) : (
            "Confirm Transfer"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AllotImprestModal;
